import { useCallback, useRef, useState } from 'react'

import { StagePicker } from '@/features/projects/components/StagePicker'
import { DueDateBadge, StatusPill, UserAvatar } from '@/shared/components/common'
import { useT } from '@/shared/i18n'
import { cn } from '@/shared/lib/cn'
import type { Stage, Task, UserSummary } from '@/shared/types/domain'

import { AssigneeCell } from './AssigneeCell'
import { ConfirmCompletion } from './ConfirmCompletion'
import { DoneToggle } from './DoneToggle'
import { DueDateCell } from './DueDateCell'

export interface TaskRowProps {
  task: Task
  /** Sin acciones: ni completar, ni abrir el detalle. */
  readOnly?: boolean
  onOpen?: (task: Task) => void
  onToggleDone?: (task: Task, done: boolean) => void
  /** Con esto, la fecha se edita en la fila. Sin esto, se muestra y ya. */
  onChangeDue?: (task: Task, date: string | null) => void
  /** Con esto, el responsable se cambia en la fila. Sin esto, se muestra y ya. */
  onChangeAssignee?: (task: Task, userId: string | null, user: UserSummary | null) => void
  /**
   * Con esto (y `stages`), el estado se cambia desde la fila. Sin esto, se
   * muestra y ya. Ir a una columna "hecho" pide la misma confirmación que la
   * casilla: también saca la tarea de las abiertas.
   */
  onMoveToStage?: (task: Task, stage: Stage) => void
  stages?: readonly Stage[]
  now?: Date
  className?: string
}

/**
 * Fila densa de tarea, para la lista DENTRO de un proyecto (ahi la empresa y el
 * proyecto ya los sabe el contexto, y repetirlos es ruido).
 */
export function TaskRow({
  task,
  readOnly,
  onOpen,
  onToggleDone,
  onChangeDue,
  onChangeAssignee,
  onMoveToStage,
  stages = [],
  now,
  className,
}: TaskRowProps) {
  const t = useT()
  const done = task.completed_at !== null
  const estado = useRef<HTMLSpanElement>(null)
  const [aHecho, setAHecho] = useState<Stage | null>(null)
  const cancelar = useCallback(() => setAHecho(null), [])
  const editable = !readOnly && onMoveToStage && stages.length > 0

  return (
    <div
      className={cn(
        'row-dense hairline-b flex items-center gap-2 hover:bg-surface-2/60',
        'transition-colors duration-[var(--motion-fast)]',
        className,
      )}
    >
      {readOnly ? null : (
        <DoneToggle
          done={done}
          title={task.title}
          onChange={(next) => onToggleDone?.(task, next)}
        />
      )}

      {editable ? (
        <span ref={estado} className="w-[104px] shrink-0">
          <StagePicker
            size="sm"
            help={t('tasks.stage.help')}
            listLabel={t('tasks.stage.change', { tarea: task.title })}
            stage={task.stage}
            stages={stages}
            onChange={(stage) =>
              stage.category === 'done' && !done ? setAHecho(stage) : onMoveToStage(task, stage)
            }
          />
          <ConfirmCompletion
            anchor={estado}
            open={aHecho !== null}
            title={task.title}
            onCancel={cancelar}
            onConfirm={() => {
              if (aHecho) onMoveToStage(task, aHecho)
              setAHecho(null)
            }}
          />
        </span>
      ) : (
        <StatusPill category={task.stage.category} className="w-[92px] justify-start">
          {task.stage.name}
        </StatusPill>
      )}

      {readOnly ? (
        <span className={cn('min-w-0 flex-1 truncate text-xs', done && 'text-text-3 line-through')}>
          {task.title}
        </span>
      ) : (
        <button
          type="button"
          onClick={() => onOpen?.(task)}
          className={cn(
            'min-w-0 flex-1 truncate text-left text-xs hover:text-signal',
            done ? 'text-text-3 line-through' : 'text-text-1',
          )}
        >
          {task.title}
        </button>
      )}

      {onChangeAssignee && !readOnly ? (
        <AssigneeCell
          user={task.assignee}
          projectId={task.project.id}
          label={t('tasks.assignee.change', { tarea: task.title })}
          align="end"
          onChange={(userId, user) => onChangeAssignee(task, userId, user)}
          className="shrink-0"
        />
      ) : (
        <UserAvatar user={task.assignee} className="shrink-0" />
      )}

      {onChangeDue && !readOnly ? (
        <DueDateCell
          value={task.due_date}
          isDone={done}
          now={now}
          onChange={(fecha) => onChangeDue(task, fecha)}
          className="w-[104px]"
        />
      ) : (
        <DueDateBadge
          date={task.due_date}
          isDone={done}
          now={now}
          className="w-[104px] shrink-0 justify-end"
        />
      )}
    </div>
  )
}
